// What? A React component for the user dashboard interface.
// Why? To provide a centralized dashboard for diagnostics and organization overview.
// How? The component's logic flow:
// 1. Initialize: Set up the page structure with mockup data
// 2. UI Management: Render cards for diagnostics, organization, and activity
// 3. Layout: Use JoyUI components following best practices

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  DialogTitle,
  Table,
  Sheet,
  Chip,
  IconButton,
  Link as JoyLink,
  Tooltip,
} from "@mui/joy";
import {
  BugReport as BugReportIcon,
  Add,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  InfoOutlined,
  PeopleOutline as SharedIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import {
  useReadUser,
  useCreateOrganization,
  useInvitationList,
  useInvitationAccept,
  useOrganizationRead,
  useOrganizationUsers,
  useRequestAccess,
  useGetResourceUsage,
  useGetUserUsage,
  useApproveAccessRequest,
  useRejectAccessRequest,
  useUserWorkspaces,
  usePublicWorkspaces,
  useJoinPublicWorkspace,
  useWorkspaceCreate,
  useWorkspaceDelete,
  useWorkspaceUserAcceptInvite,
  useWorkspaceUserRemove,
  useDiscoverOrganization,
  useWorkspaceCapacity,
  WorkspaceCapacity,
} from "../services/api";
import {
  User as UserType,
  Invitation,
  AccessRequest,
  UserWorkspacesType,
  PublicWorkspaceType,
} from "../common/types";
import PageLoading from "../components/PageLoading";
import PageError from "../components/PageError";
import DiagnosticsCard from "../components/Dashboard/DiagnosticsCard";
import PublicWorkspacesSection from "../components/Dashboard/PublicWorkspacesSection";
import OrganizationCard from "../components/Dashboard/OrganizationCard";
import CreateOrganizationModal from "../components/Dashboard/CreateOrganizationModal";
import RequestAccessModal from "../components/Dashboard/RequestAccessModal";
import AccessRequestsList from "../components/Dashboard/AccessRequestsList";
import ApproveAccessRequestModal from "../components/Dashboard/ApproveAccessRequestModal";
import RejectAccessRequestModal from "../components/Dashboard/RejectAccessRequestModal";
import AddWorkspaceModal from "../components/Modals/AddWorkspaceModal";
import ConfirmationModal from "../components/Modals/ConfirmationModal";
import RecentlyDeletedWorkspaces from "./RecentlyDeletedWorkspaces";
import { DELETE_WORKSPACE_MESSAGE } from "./workspaceRecoveryCopy";
import { isDevFeaturesEnabled } from "../utils/devFeatures";
import { moduleRegistry } from "../modules/moduleRegistry";
import { getModuleIcon } from "../components/Dashboard/moduleIcons";
import { useTutorialTarget, useTutorialHint } from "../components/Tutorial";

const UserDashboard: React.FC = () => {
  const readUserAPI = useReadUser();
  const createOrganizationAPI = useCreateOrganization();
  const invitationListAPI = useInvitationList();
  const invitationAcceptAPI = useInvitationAccept();
  const organizationReadAPI = useOrganizationRead();
  const organizationUsersAPI = useOrganizationUsers();
  const requestAccessAPI = useRequestAccess();
  const getResourceUsageAPI = useGetResourceUsage();
  const getUserUsageAPI = useGetUserUsage();
  const approveAccessRequestAPI = useApproveAccessRequest();
  const rejectAccessRequestAPI = useRejectAccessRequest();
  const userWorkspacesAPI = useUserWorkspaces();
  const publicWorkspacesAPI = usePublicWorkspaces();
  const joinPublicWorkspaceAPI = useJoinPublicWorkspace();
  const workspaceCreateAPI = useWorkspaceCreate();
  const workspaceDeleteAPI = useWorkspaceDelete();
  const removeWorkspaceUser = useWorkspaceUserRemove();
  const acceptWorkspaceInvite = useWorkspaceUserAcceptInvite();
  const discoverOrganizationAPI = useDiscoverOrganization();
  const getWorkspaceCapacity = useWorkspaceCapacity();

  // User data state
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create organization modal state
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [tempOrgName, setTempOrgName] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [createOrgError, setCreateOrgError] = useState<string | null>(null);

  // Invitations state
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>(
    [],
  );
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState<
    string | null
  >(null);
  const [invitationError, setInvitationError] = useState<
    Record<string, string>
  >({});
  const [organizationNames, setOrganizationNames] = useState<
    Record<string, string>
  >({});

  // Access request state (for users without org)
  const [, setLoadingAvailableOrgs] = useState(false);
  const [availableOrganizations, setAvailableOrganizations] = useState<any[]>(
    [],
  );

  const [requestAccessModalOpen, setRequestAccessModalOpen] = useState(false);
  const [orgToRequestAccess, setOrgToRequestAccess] = useState<any | null>(
    null,
  );
  const [requestMessage, setRequestMessage] = useState("");
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [requestAccessError, setRequestAccessError] = useState<string | null>(
    null,
  );

  // Resource usage state (organization-wide, for admins)
  const [resourceUsage, setResourceUsage] = useState<any>(null);
  const [loadingResourceUsage, setLoadingResourceUsage] = useState(false);

  // User usage state (personal, for all users)
  const [userUsage, setUserUsage] = useState<any>(null);
  const [loadingUserUsage, setLoadingUserUsage] = useState(false);

  // Access request state (for admins)
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [loadingAccessRequests, setLoadingAccessRequests] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState<string | null>(null);
  const [rejectingRequest, setRejectingRequest] = useState<string | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [requestToApprove, setRequestToApprove] =
    useState<AccessRequest | null>(null);
  const [requestToReject, setRequestToReject] = useState<AccessRequest | null>(
    null,
  );
  const [approveRole, setApproveRole] = useState<"admin" | "member">("member");
  const [rejectReason, setRejectReason] = useState("");

  // Debug panel state
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [debugUsers, setDebugUsers] = useState<any[]>([]);
  const [debugLoading, setDebugLoading] = useState(false);

  // Workspaces state
  const [userWorkspaces, setUserWorkspaces] = useState<UserWorkspacesType[]>(
    [],
  );
  const [publicWorkspaces, setPublicWorkspaces] = useState<
    PublicWorkspaceType[]
  >([]);
  const [joiningPublicWorkspace, setJoiningPublicWorkspace] = useState<
    string | null
  >(null);
  const [publicWorkspaceError, setPublicWorkspaceError] = useState<
    string | null
  >(null);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [workspacesFetchError, setWorkspacesFetchError] =
    useState<Error | null>(null);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [workspaceDeleteModalOpen, setWorkspaceDeleteModalOpen] =
    useState(false);
  const [workspaceLeaveModalOpen, setWorkspaceLeaveModalOpen] = useState(false);
  const [isProcessingWorkspace, setIsProcessingWorkspace] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<string | null>(
    null,
  );
  const [workspaceToLeave, setWorkspaceToLeave] = useState<string | null>(null);
  const [workspaceLastFetchTime, setWorkspaceLastFetchTime] =
    useState<number>(0);
  const [recoveryRefreshKey, setRecoveryRefreshKey] = useState(0);
  const FETCH_COOLDOWN = 1000;
  const [upgradeModalOpen, setUpgradeModalOpen] = useState<boolean>(false);
  const [capacityInfo, setCapacityInfo] = useState<WorkspaceCapacity | null>(
    null,
  );
  const [checkingCapacity, setCheckingCapacity] = useState<boolean>(false);
  const canCreate = capacityInfo === null || capacityInfo.can_create;

  // Pre-fetch workspace capacity on mount to show disabled state proactively
  useEffect(() => {
    getWorkspaceCapacity()
      .then(setCapacityInfo)
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        const userData = await readUserAPI();
        setUser(userData);
      } catch (err: any) {
        setError(err.message || "Failed to load user data");
        console.error("Error fetching user:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - readUserAPI function is stable

  // Fetch pending invitations for the current user
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!user) return;

      try {
        setLoadingInvitations(true);

        // Check if user has a pending invitation (status = 'invited')
        if (
          user.status === "invited" &&
          user.organization_id &&
          user.invitation_token
        ) {
          // User is invited - create invitation from user object
          const invitation: Invitation = {
            id: user.id, // Use user ID as invitation ID
            email: user.email,
            organization_id: user.organization_id,
            organization_name: user.organization_name || "Unknown Organization",
            role: user.role_in_org || "member",
            status: "pending",
            token: user.invitation_token,
            expires_at: user.invitation_expires_at || "",
            message: user.invitation_message || undefined,
            user_id: user.id,
            invited_by: user.invited_by || "",
            invited_by_email: "", // Not available in user object
            created_at: user.access_request_date || new Date().toISOString(),
          };

          // Check if invitation is expired
          const now = new Date();
          const expiresAt = new Date(invitation.expires_at);
          if (expiresAt > now) {
            setPendingInvitations([invitation]);
            setOrganizationNames({
              [user.organization_id]:
                user.organization_name || "Unknown Organization",
            });
          } else {
            // Invitation expired
            setPendingInvitations([]);
            setOrganizationNames({});
          }
        } else {
          // User not invited - only fetch invitations if user has organization and is admin
          // (invitationListAPI requires organization membership)
          if (hasOrganization && isAdmin) {
            const invitations = await invitationListAPI(undefined, "pending");

            // Filter invitations that match user's email or have user_id matching current user
            const userInvitations = invitations.filter(
              (inv: Invitation) =>
                inv.email === user.email || inv.user_id === user.id,
            );

            // Filter out expired invitations
            const now = new Date();
            const activeInvitations = userInvitations.filter(
              (inv: Invitation) => {
                const expiresAt = new Date(inv.expires_at);
                return expiresAt > now;
              },
            );
            setPendingInvitations(activeInvitations);

            // Extract organization names from invitations (they already include organization_name)
            if (activeInvitations.length > 0) {
              const orgNames: Record<string, string> = {};
              activeInvitations.forEach((inv: Invitation) => {
                if (inv.organization_id && inv.organization_name) {
                  orgNames[inv.organization_id] = inv.organization_name;
                }
              });
              setOrganizationNames(orgNames);
            } else {
              setOrganizationNames({});
            }
          } else {
            // User doesn't have organization or is not admin - no invitations to show
            setPendingInvitations([]);
            setOrganizationNames({});
          }
        }
      } catch (err: any) {
        console.error("Error fetching invitations:", err);
        setPendingInvitations([]);
        setOrganizationNames({});
      } finally {
        setLoadingInvitations(false);
      }
    };

    fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email, user?.status]); // Added status to re-fetch when user status changes

  // Organization details state (fetched separately since /api/user/read doesn't include it)
  const [organizationDetails, setOrganizationDetails] = useState<{
    name: string;
    status: string;
  } | null>(null);
  const [loadingOrganizationDetails, setLoadingOrganizationDetails] =
    useState(false);

  // Derived values (needed for useEffect below)
  // User has an organization only if they are verified (not just requesting access)
  const hasOrganization =
    user?.organization_id !== null &&
    user?.organization_id !== undefined &&
    user?.status === "verified";
  const hasPendingAccessRequest =
    user?.organization_id !== null &&
    user?.organization_id !== undefined &&
    user?.status === "access_requested";
  const isAdmin = user?.role_in_org === "admin";
  const orgStatus =
    organizationDetails?.status || user?.organization_status || undefined;
  const isActiveOrganization = hasOrganization && orgStatus === "active";

  useEffect(() => {
    if (isActiveOrganization) {
      fetchPublicWorkspaces();
    } else {
      setPublicWorkspaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveOrganization, user?.organization_id]);

  // Debug logging - check browser console (F12) to see these values (dev mode only)
  useEffect(() => {
    if (user && isDevFeaturesEnabled()) {
      console.log("🔍 User Dashboard Debug:", {
        status: user.status,
        organization_id: user.organization_id,
        hasOrganization,
        hasPendingAccessRequest,
        isAdmin,
        organizationName: organizationDetails?.name,
      });
    }
  }, [
    user,
    hasOrganization,
    hasPendingAccessRequest,
    isAdmin,
    organizationDetails,
  ]);

  // Fetch available organizations for users without org (with verified domains matching user's email)
  useEffect(() => {
    const fetchAvailableOrganizations = async () => {
      // Don't fetch if user has organization, has pending request, or no email
      if (!user || hasOrganization || hasPendingAccessRequest || !user.email)
        return;

      try {
        setLoadingAvailableOrgs(true);
        // Use the discover endpoint to find organizations with verified domains matching user's email
        const data = await discoverOrganizationAPI(user.email);

        if (data.organization_found && data.organization) {
          // Map backend response to frontend expected format
          setAvailableOrganizations([
            {
              organization_id: data.organization.id,
              organization_name: data.organization.name,
              verified_domains: data.organization.verified_domains || [],
            },
          ]);
        } else {
          setAvailableOrganizations([]);
        }
      } catch (err: any) {
        // Silently handle error - it's normal for users without matching domains
        // to not have any organizations to discover
        console.debug(
          "No organizations discovered for user email domain:",
          user.email,
        );
        setAvailableOrganizations([]);
      } finally {
        setLoadingAvailableOrgs(false);
      }
    };

    fetchAvailableOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email, hasOrganization, hasPendingAccessRequest]);

  // Fetch user usage (personal metrics, for all users with organization)
  useEffect(() => {
    if (!user?.organization_id || !hasOrganization) return;

    const fetchUserUsage = async () => {
      try {
        setLoadingUserUsage(true);
        const data = await getUserUsageAPI();
        setUserUsage(data);
      } catch (err: any) {
        console.error("Error fetching user usage:", err);
        // Silently fail - user usage is optional
      } finally {
        setLoadingUserUsage(false);
      }
    };

    fetchUserUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organization_id, hasOrganization]);

  // Fetch resource usage (organization-wide, only for admins with active organization)
  useEffect(() => {
    if (!isAdmin || !user?.organization_id || !isActiveOrganization) return;

    const fetchResourceUsage = async () => {
      try {
        setLoadingResourceUsage(true);
        const data = await getResourceUsageAPI();
        setResourceUsage(data);
      } catch (err: any) {
        console.error("Error fetching resource usage:", err);
      } finally {
        setLoadingResourceUsage(false);
      }
    };

    fetchResourceUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.organization_id, isActiveOrganization]); // Fetch when admin status changes

  // Fetch organization details (only for verified admins)
  // Users with pending access requests use user.organization_name instead
  useEffect(() => {
    // Only fetch if user is a verified admin with organization
    // Users with pending access requests shouldn't access the dashboard
    if (!user?.organization_id || !isAdmin || !hasOrganization) {
      setOrganizationDetails(null);
      return;
    }

    const fetchOrganizationDetails = async () => {
      try {
        setLoadingOrganizationDetails(true);
        const dashboard = await organizationReadAPI();
        if (dashboard?.organization) {
          setOrganizationDetails({
            name: dashboard.organization.name,
            status: dashboard.organization.status,
          });
        }
      } catch (err: any) {
        // Silently fail - organization details are optional
        console.error("Error fetching organization details:", err);
      } finally {
        setLoadingOrganizationDetails(false);
      }
    };

    fetchOrganizationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, hasOrganization, user?.organization_id]); // organizationReadAPI is stable, no need to include in deps

  // Fetch access requests (only for admins)
  useEffect(() => {
    if (!isAdmin || !hasOrganization || !user?.organization_id) return;

    const fetchAccessRequests = async () => {
      const orgId = user.organization_id;
      if (!orgId) return;

      try {
        setLoadingAccessRequests(true);
        const allMembers = await organizationUsersAPI(orgId);
        const requests = allMembers
          .filter((m: UserType) => m.status === "access_requested")
          .map((m: UserType) => ({
            user_id: m.id,
            email: m.email,
            organization_id: orgId,
            organization_name: organizationDetails?.name || "",
            access_request_message: m.access_request_message || null,
            access_request_date:
              m.access_request_date || new Date().toISOString(),
            status: "access_requested" as const,
          }));
        setAccessRequests(requests);
      } catch (err: any) {
        console.error("Error fetching access requests:", err);
      } finally {
        setLoadingAccessRequests(false);
      }
    };

    fetchAccessRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, hasOrganization, user?.organization_id]);

  // Refresh user data
  const refreshUser = async () => {
    try {
      const userData = await readUserAPI();
      setUser(userData);
    } catch (err: any) {
      console.error("Error refreshing user:", err);
    }
  };

  // Fetch debug users
  const fetchDebugUsers = async () => {
    try {
      setDebugLoading(true);
      const response = await fetch("http://localhost:3001/api/dev/mock-users");
      const data = await response.json();
      setDebugUsers(data.users || []);
    } catch (err) {
      console.error("Error fetching debug users:", err);
    } finally {
      setDebugLoading(false);
    }
  };

  // Reset single user
  const resetUser = async (email: string) => {
    try {
      await fetch("http://localhost:3001/api/dev/reset-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      await refreshUser();
      await fetchDebugUsers();
    } catch (err) {
      console.error("Error resetting user:", err);
    }
  };

  // Reset all users
  const resetAllUsers = async () => {
    try {
      await fetch("http://localhost:3001/api/dev/reset-all-users", {
        method: "POST",
      });
      await refreshUser();
      await fetchDebugUsers();
    } catch (err) {
      console.error("Error resetting all users:", err);
    }
  };

  const handleCreateOrg = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!tempOrgName.trim()) {
      setCreateOrgError("Organization name is required");
      return;
    }

    try {
      setCreatingOrg(true);
      setCreateOrgError(null);

      // Extract domain from user's email if available
      const userEmail = user?.email || "";
      const primaryDomain = userEmail.includes("@")
        ? userEmail.split("@")[1]
        : undefined;

      await createOrganizationAPI({
        name: tempOrgName.trim(),
        primary_domain: primaryDomain,
      });

      // Refresh user data to get updated organization info
      await refreshUser();

      // Close modal and reset form
      setIsOrgModalOpen(false);
      setTempOrgName("");
    } catch (err: any) {
      console.error("Error creating organization:", err);
      // Handle different error types
      if (err.message?.includes("already belongs")) {
        setCreateOrgError(
          "You already belong to an organization. Please refresh the page.",
        );
      } else if (err.message?.includes("Missing required")) {
        setCreateOrgError("Organization name is required.");
      } else {
        setCreateOrgError(
          err.message ||
            "Failed to create organization. Please try again or contact support if the problem persists.",
        );
      }
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleCloseOrgModal = () => {
    setIsOrgModalOpen(false);
    setTempOrgName("");
    setCreateOrgError(null);
  };

  // Handle invitation acceptance
  const handleAcceptInvitation = async (invitation: Invitation) => {
    try {
      setProcessingInvitation(invitation.id);
      setInvitationError((prev) => ({ ...prev, [invitation.id]: "" }));
      await invitationAcceptAPI({ token: invitation.token });
      // Refresh user data to get updated organization info
      await refreshUser();
      // Remove accepted invitation from list
      setPendingInvitations((prev) =>
        prev.filter((inv) => inv.id !== invitation.id),
      );
      // Clear error for this invitation
      setInvitationError((prev) => {
        const updated = { ...prev };
        delete updated[invitation.id];
        return updated;
      });
    } catch (err: any) {
      console.error("Error accepting invitation:", err);

      // Extract error message from different error formats
      let errorMessage = "Failed to accept invitation. Please try again.";

      if (err.response?.detail) {
        // FastAPI HTTPException detail
        if (typeof err.response.detail === "string") {
          errorMessage = err.response.detail;
        } else if (Array.isArray(err.response.detail)) {
          // Pydantic validation errors - extract messages
          errorMessage = err.response.detail
            .map((e: any) => e.msg || JSON.stringify(e))
            .join(", ");
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setInvitationError((prev) => ({
        ...prev,
        [invitation.id]: errorMessage,
      }));
    } finally {
      setProcessingInvitation(null);
    }
  };

  // Helper to calculate days until expiration
  const getDaysUntilExpiration = (expiresAt: string): number => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper to check if invitation is expired
  const isInvitationExpired = (expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date();
  };

  // Handle opening request access modal
  const handleOpenRequestAccessModal = (org: any) => {
    setOrgToRequestAccess(org);
    setRequestAccessModalOpen(true);
    setRequestMessage("");
    setRequestAccessError(null);
  };

  // Handle closing request access modal
  const handleCloseRequestAccessModal = () => {
    setRequestAccessModalOpen(false);
    setOrgToRequestAccess(null);
    setRequestMessage("");
    setRequestAccessError(null);
  };

  // Handle requesting access
  const handleRequestAccess = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!orgToRequestAccess) return;

    try {
      setRequestingAccess(true);
      setRequestAccessError(null);

      await requestAccessAPI(orgToRequestAccess.organization_id, {
        email: user?.email || "",
        message: requestMessage.trim() || undefined,
      });

      // Refresh user data to get updated status
      await refreshUser();

      // Refresh available organizations
      if (user?.email) {
        try {
          const data = await discoverOrganizationAPI(user.email);
          if (data.organization_found && data.organization) {
            // Map backend response to frontend expected format
            setAvailableOrganizations([
              {
                organization_id: data.organization.id,
                organization_name: data.organization.name,
                verified_domains: data.organization.verified_domains || [],
              },
            ]);
          } else {
            setAvailableOrganizations([]);
          }
        } catch (err: any) {
          console.error("Error refreshing available organizations:", err);
        }
      }

      // Close modal
      handleCloseRequestAccessModal();
    } catch (err: any) {
      console.error("Error requesting access:", err);

      // Extract detailed error message from response
      let errorMessage = "Failed to request access. Please try again.";

      if (err.response) {
        // Try multiple ways to extract the error message
        if (typeof err.response === "string") {
          errorMessage = err.response;
        } else if (err.response.detail) {
          // Handle FastAPI/Pydantic validation errors (array format)
          if (Array.isArray(err.response.detail)) {
            const errors = err.response.detail
              .map((e: any) => {
                // Extract the user-friendly message and field name
                const field =
                  e.loc && e.loc.length > 0 ? e.loc[e.loc.length - 1] : "field";
                const message = e.msg || e.message || "Invalid value";
                // If there's a ctx.reason, use that as it's more specific
                const reason = e.ctx?.reason;
                return reason ? `${field}: ${reason}` : `${field}: ${message}`;
              })
              .join("; ");
            errorMessage = errors || "Validation error";
          } else if (typeof err.response.detail === "string") {
            errorMessage = err.response.detail;
          } else {
            errorMessage = JSON.stringify(err.response.detail);
          }
        } else if (err.response.message) {
          errorMessage = err.response.message;
        } else if (err.response.error) {
          errorMessage = err.response.error;
        } else if (typeof err.response === "object") {
          // Try to extract all string values from the response
          const values = Object.values(err.response).filter(
            (v) => typeof v === "string",
          );
          if (values.length > 0) {
            errorMessage = values.join(", ");
          } else {
            errorMessage = JSON.stringify(err.response);
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setRequestAccessError(errorMessage);
    } finally {
      setRequestingAccess(false);
    }
  };

  // Access request handlers (for admins)
  const handleOpenApproveModal = (request: AccessRequest) => {
    setRequestToApprove(request);
    setApproveRole("member");
    setApproveModalOpen(true);
  };

  const handleCloseApproveModal = () => {
    setApproveModalOpen(false);
    setRequestToApprove(null);
    setApproveRole("member");
  };

  const handleApproveAccessRequest = async () => {
    if (!requestToApprove || !user?.organization_id) return;

    setApprovingRequest(requestToApprove.user_id);

    try {
      await approveAccessRequestAPI(user.organization_id, {
        user_id: requestToApprove.user_id,
        role: approveRole,
      });

      // Refresh access requests
      const allMembers = await organizationUsersAPI(user.organization_id);
      const requests = allMembers
        .filter((m: UserType) => m.status === "access_requested")
        .map((m: UserType) => ({
          user_id: m.id,
          email: m.email,
          organization_id: user.organization_id!,
          organization_name: organizationDetails?.name || "",
          access_request_message: m.access_request_message || null,
          access_request_date:
            m.access_request_date || new Date().toISOString(),
          status: "access_requested" as const,
        }));
      setAccessRequests(requests);

      // Refresh user data
      await refreshUser();

      handleCloseApproveModal();
    } catch (err: any) {
      console.error("Failed to approve access request:", err);
    } finally {
      setApprovingRequest(null);
    }
  };

  const handleOpenRejectModal = (request: AccessRequest) => {
    setRequestToReject(request);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRequestToReject(null);
    setRejectReason("");
  };

  const handleRejectAccessRequest = async () => {
    if (!requestToReject || !user?.organization_id) return;

    setRejectingRequest(requestToReject.user_id);

    try {
      await rejectAccessRequestAPI(user.organization_id, {
        user_id: requestToReject.user_id,
        reason: rejectReason || undefined,
      });

      // Refresh access requests
      const allMembers = await organizationUsersAPI(user.organization_id);
      const requests = allMembers
        .filter((m: UserType) => m.status === "access_requested")
        .map((m: UserType) => ({
          user_id: m.id,
          email: m.email,
          organization_id: user.organization_id!,
          organization_name: organizationDetails?.name || "",
          access_request_message: m.access_request_message || null,
          access_request_date:
            m.access_request_date || new Date().toISOString(),
          status: "access_requested" as const,
        }));
      setAccessRequests(requests);

      // Refresh user data
      await refreshUser();

      handleCloseRejectModal();
    } catch (err: any) {
      console.error("Failed to reject access request:", err);
    } finally {
      setRejectingRequest(null);
    }
  };

  // Workspace management functions
  const fetchWorkspaces = async (force: boolean = false) => {
    const now = Date.now();
    if (!force && now - workspaceLastFetchTime < FETCH_COOLDOWN) {
      return;
    }

    try {
      setLoadingWorkspaces(true);
      const data = await userWorkspacesAPI();
      if (data) {
        setUserWorkspaces(data);
      }
      // Successful fetch — clear any prior error so the retry block is hidden
      setWorkspacesFetchError(null);
      setWorkspaceLastFetchTime(now);
    } catch (error: any) {
      // Top-of-page banner is already raised by useAuthFetch via the
      // ErrorMessageProvider; we additionally track the error locally so
      // the empty-state render can distinguish "fetch failed" from
      // "user has 0 workspaces" instead of collapsing both into the
      // welcome CTA.
      console.error("Failed to fetch workspaces:", error);
      setUserWorkspaces([]);
      setWorkspacesFetchError(
        error instanceof Error ? error : new Error(String(error)),
      );
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const fetchPublicWorkspaces = async () => {
    try {
      setPublicWorkspaces(await publicWorkspacesAPI());
      setPublicWorkspaceError(null);
    } catch (error: any) {
      setPublicWorkspaceError(
        error instanceof Error
          ? error.message
          : "Couldn't load public workspaces.",
      );
    }
  };

  const handleJoinPublicWorkspace = async (workspaceId: string) => {
    setJoiningPublicWorkspace(workspaceId);
    setPublicWorkspaceError(null);
    try {
      await joinPublicWorkspaceAPI(workspaceId);
      await Promise.all([fetchWorkspaces(true), fetchPublicWorkspaces()]);
    } catch (error: any) {
      setPublicWorkspaceError(
        error instanceof Error
          ? error.message
          : "Couldn't join this workspace.",
      );
    } finally {
      setJoiningPublicWorkspace(null);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddWorkspace = async (
    workspaceName: string,
    selectedModules: string[],
  ) => {
    setCreatingWorkspace(true);
    try {
      await workspaceCreateAPI(workspaceName, selectedModules);
      await fetchWorkspaces(true);
      getWorkspaceCapacity()
        .then(setCapacityInfo)
        .catch(() => {});
    } catch (error: any) {
      console.error("Failed to create workspace:", error);
    } finally {
      setCreatingWorkspace(false);
      setWorkspaceModalOpen(false);
    }
  };

  const openDeleteWorkspaceModal = (workspaceId: string) => {
    setWorkspaceToDelete(workspaceId);
    setWorkspaceDeleteModalOpen(true);
  };

  const handleDeleteWorkspaceConfirmed = async () => {
    setIsProcessingWorkspace(true);
    try {
      if (workspaceToDelete) {
        await workspaceDeleteAPI(workspaceToDelete);
        await fetchWorkspaces(true);
        setRecoveryRefreshKey((key) => key + 1);
        getWorkspaceCapacity()
          .then(setCapacityInfo)
          .catch(() => {});
      }
    } catch (error: any) {
      console.error("Failed to delete workspace:", error);
    } finally {
      setIsProcessingWorkspace(false);
      setWorkspaceDeleteModalOpen(false);
    }
  };

  const openLeaveWorkspaceModal = (workspaceId: string) => {
    setWorkspaceToLeave(workspaceId);
    setWorkspaceLeaveModalOpen(true);
  };

  const handleLeaveWorkspaceConfirmed = async () => {
    setIsProcessingWorkspace(true);
    try {
      if (workspaceToLeave && user?.id) {
        await removeWorkspaceUser(workspaceToLeave, user.id);
        await fetchWorkspaces(true);
        getWorkspaceCapacity()
          .then(setCapacityInfo)
          .catch(() => {});
      }
    } catch (error: any) {
      console.error("Failed to leave workspace:", error);
    } finally {
      setIsProcessingWorkspace(false);
      setWorkspaceLeaveModalOpen(false);
    }
  };

  const handleAcceptWorkspaceInvite = async (workspace_id: string) => {
    try {
      await acceptWorkspaceInvite(workspace_id);
      await fetchWorkspaces(true);
    } catch (error: any) {
      console.error("Failed to accept invite", error);
    }
  };

  const handleRemoveWorkspaceUser = async (
    workspace_id: string,
    user_id: string,
  ) => {
    try {
      await removeWorkspaceUser(workspace_id, user_id);
      await fetchWorkspaces(true);
    } catch (error: any) {
      console.error("Failed to remove user", error);
    }
  };

  const handleAddNewClick = async () => {
    setCheckingCapacity(true);
    try {
      const capacity = await getWorkspaceCapacity();
      setCapacityInfo(capacity);
      if (capacity.can_create) {
        setWorkspaceModalOpen(true);
      } else {
        setUpgradeModalOpen(true);
      }
    } catch (error) {
      console.error("Failed to check workspace capacity:", error);
      // Fallback: open the modal anyway
      setWorkspaceModalOpen(true);
    } finally {
      setCheckingCapacity(false);
    }
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Get module display name from registry
  const getModuleDisplayName = (moduleId: string): string => {
    return moduleRegistry[moduleId]?.name || moduleId;
  };

  const getPermissions = (workspace: UserWorkspacesType) => {
    const hasAccepted = workspace?.permissions?.status === "accepted";
    const role = workspace?.permissions?.role;

    const isOwner = role === "owner";
    const isAdmin = role === "admin" || isOwner;
    const isEditor = role === "editor" || isAdmin;
    const isUser = role === "user" || isEditor;
    const isOnlyOwner = workspace?.permissions?.is_only_owner;

    return {
      isOwner: isOwner && hasAccepted,
      isAdmin: isAdmin && hasAccepted,
      isEditor: isEditor && hasAccepted,
      isUser: isUser && hasAccepted,
      isOnlyOwner,
    };
  };

  const canUserLeaveWorkspace = (workspace: UserWorkspacesType) => {
    const permissions = getPermissions(workspace);
    if (permissions.isOwner) {
      return !permissions.isOnlyOwner;
    }
    return true;
  };

  // Tutorial: highlight "Add New" when the user has an active org but no workspaces yet.
  // userWorkspaces starts as [] and loadingWorkspaces as false, so "empty" is
  // indistinguishable from "not fetched yet" until the first successful load.
  // workspaceLastFetchTime is only set on success, so it doubles as the settled
  // signal; workspacesFetchError keeps a failed fetch from reading as "0 workspaces"
  // (the same distinction the empty-state render below relies on).
  const workspacesSettled =
    workspaceLastFetchTime > 0 && !loadingWorkspaces && !workspacesFetchError;
  const addWorkspaceButtonRef = useTutorialTarget("dashboard-add-workspace");
  useTutorialHint(
    {
      id: "first-workspace-prompt",
      targetId: "dashboard-add-workspace",
      title: "Create your first workspace",
      body: 'Click "Add New" to create a workspace and start collaborating.',
      placement: "bottom",
    },
    isActiveOrganization && workspacesSettled && userWorkspaces.length === 0,
  );

  // Separate workspaces by status
  const activeWorkspaces = userWorkspaces.filter(
    (workspace) => workspace.status !== "paused",
  );
  const pausedWorkspaces = userWorkspaces.filter(
    (workspace) => workspace.status === "paused",
  );

  const myPausedWorkspaces = pausedWorkspaces.filter(
    (workspace) => getPermissions(workspace).isOwner,
  );
  const otherPausedWorkspaces = pausedWorkspaces.filter(
    (workspace) => !getPermissions(workspace).isOwner,
  );

  // Group all active workspaces by module for the consolidated view
  const workspacesByModule = React.useMemo(() => {
    const groups: Record<string, UserWorkspacesType[]> = {};
    for (const workspace of activeWorkspaces) {
      const modules = (workspace as any).enabled_modules as
        string[] | undefined;
      const moduleId = modules && modules.length > 0 ? modules[0] : "none";
      if (!groups[moduleId]) groups[moduleId] = [];
      groups[moduleId].push(workspace);
    }
    // Sort groups: known modules first (alphabetical by display name), "none" last
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "none") return 1;
      if (b === "none") return -1;
      return getModuleDisplayName(a).localeCompare(getModuleDisplayName(b));
    });
    return sortedKeys.map((key) => ({
      moduleId: key,
      moduleName: key === "none" ? "Unconfigured" : getModuleDisplayName(key),
      workspaces: [...groups[key]].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [activeWorkspaces]);

  // Shared workspace row component -- used for both desktop and mobile
  const WorkspaceRow = ({
    workspace,
    even = true,
  }: {
    workspace: UserWorkspacesType;
    even?: boolean;
  }) => {
    const permissions = getPermissions(workspace);
    const isOwned = permissions.isOwner;
    const isInvited = workspace.permissions.status === "invited";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 1.25,
          px: 2,
          borderRadius: "sm",
          bgcolor: even ? "transparent" : "background.level1",
          transition: "background-color 0.15s ease",
          "&:hover": {
            bgcolor: "background.level2",
          },
          "&:hover .workspace-actions": {
            opacity: 1,
          },
        }}
      >
        {/* Name + shared indicator */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {isInvited ? (
            <Typography level="body-md" sx={{ fontWeight: 500 }}>
              {workspace.name}
            </Typography>
          ) : (
            <JoyLink
              component={Link}
              to={`/workspace/${workspace.id}/chat`}
              level="body-md"
              sx={{
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {workspace.name}
            </JoyLink>
          )}
          {!isOwned && !isInvited && (
            <Tooltip
              title={`Shared with you as ${capitalizeFirstLetter(workspace.permissions.role)}`}
            >
              <Chip
                size="sm"
                variant="soft"
                color="neutral"
                startDecorator={<SharedIcon sx={{ fontSize: 13 }} />}
                sx={{ fontSize: "0.7rem", height: 22 }}
              >
                {capitalizeFirstLetter(workspace.permissions.role)}
              </Chip>
            </Tooltip>
          )}
        </Box>

        {/* Invite action or edit/delete actions */}
        {isInvited ? (
          <Button
            size="sm"
            variant="soft"
            color="warning"
            onClick={() => handleAcceptWorkspaceInvite(workspace.id)}
          >
            Accept Invite
          </Button>
        ) : (
          <Stack
            className="workspace-actions"
            direction="row"
            spacing={0.5}
            sx={{ opacity: { xs: 1, md: 0 }, transition: "opacity 0.15s ease" }}
          >
            {permissions.isOwner && (
              <Tooltip title="Delete workspace">
                <IconButton
                  onClick={() => openDeleteWorkspaceModal(workspace.id)}
                  size="sm"
                  variant="plain"
                  color="danger"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isOwned &&
              (workspace.permissions.status === "accepted" ? (
                canUserLeaveWorkspace(workspace) && (
                  <Tooltip title="Leave workspace">
                    <IconButton
                      onClick={() => openLeaveWorkspaceModal(workspace.id)}
                      size="sm"
                      variant="plain"
                      color="neutral"
                    >
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              ) : (
                <Tooltip title="Decline invite">
                  <IconButton
                    onClick={() =>
                      handleRemoveWorkspaceUser(workspace.id, user?.id || "")
                    }
                    size="sm"
                    variant="plain"
                    color="neutral"
                  >
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ))}
          </Stack>
        )}
      </Box>
    );
  };

  // Open debug panel and fetch users
  const handleOpenDebugPanel = async () => {
    setIsDebugPanelOpen(true);
    await fetchDebugUsers();
  };

  const handleAddFakePausedWorkspaces = () => {
    const fakeWorkspaces: UserWorkspacesType[] = [
      {
        id: "fake-paused-1",
        name: "Paused Workspace 1",
        role: "owner",
        status: "paused",
        subscription: {},
        permissions: {
          role: "owner",
          user_id: user?.id || "",
          status: "accepted",
          is_only_owner: true,
        },
        indexing_status: undefined,
      } as any,
      {
        id: "fake-paused-2",
        name: "Another Paused Workspace",
        role: "editor",
        status: "paused",
        subscription: {},
        permissions: {
          role: "editor",
          user_id: user?.id || "",
          status: "accepted",
          is_only_owner: false,
        },
        indexing_status: undefined,
      } as any,
    ];

    // Add enabled_modules to fake workspaces
    (fakeWorkspaces[0] as any).enabled_modules = ["bkg", "meridian"];
    (fakeWorkspaces[1] as any).enabled_modules = ["meridian"];

    setUserWorkspaces((prev) => [...prev, ...fakeWorkspaces]);
  };

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return <PageError>Error: {error}</PageError>;
  }

  if (!user) {
    return <PageError>No user data available</PageError>;
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Dashboard</Typography>
        {isDevFeaturesEnabled() && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              startDecorator={<BugReportIcon />}
              onClick={handleOpenDebugPanel}
            >
              Debug Panel
            </Button>
          </Stack>
        )}
      </PageHeader>

      <Stack spacing={3}>
        {/* Billing Setup Prompt - Show when organization is not active (Admin Only) */}
        {hasOrganization && isAdmin && orgStatus && orgStatus !== "active" && (
          <Alert
            color={orgStatus === "pending_payment" ? "warning" : "neutral"}
            variant="soft"
            sx={{
              borderRadius: "sm",
              border: "1px solid",
              borderColor:
                orgStatus === "pending_payment"
                  ? "var(--joy-palette-warning-300)"
                  : "var(--joy-palette-neutral-300)",
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography level="body-md" sx={{ mb: 1, fontWeight: 600 }}>
                  {orgStatus === "pending_payment"
                    ? "Complete Billing Setup to Activate Your Organization"
                    : orgStatus === "pending_setup"
                      ? "Setup Required"
                      : "Organization Suspended"}
                </Typography>
                <Typography level="body-sm">
                  {orgStatus === "pending_payment"
                    ? "Choose a billing package to activate your organization and start inviting users."
                    : orgStatus === "pending_setup"
                      ? "Complete the setup process to activate your organization."
                      : "Your organization has been suspended. Please contact support for assistance."}
                </Typography>
              </Box>
              {(orgStatus === "pending_payment" ||
                orgStatus === "pending_setup") && (
                <Button
                  variant="solid"
                  color="primary"
                  component={Link}
                  to="/subscription/select-package"
                  size="md"
                  sx={{ alignSelf: "flex-start" }}
                >
                  Go to Billing & Subscriptions
                </Button>
              )}
            </Stack>
          </Alert>
        )}

        {/* Pending Invitations Card - Show if user has pending invitations AND already belongs to an org */}
        {/* (If no org, invitations will be shown in the Organization card instead) */}
        {pendingInvitations.length > 0 && hasOrganization && (
          <Card>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2 }}>
                Pending Invitations
              </Typography>

              {loadingInvitations ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <CircularProgress size="sm" />
                </Box>
              ) : (
                <Stack spacing={2}>
                  {pendingInvitations.map((invitation) => {
                    const daysUntilExpiration = getDaysUntilExpiration(
                      invitation.expires_at,
                    );
                    const isExpired = isInvitationExpired(
                      invitation.expires_at,
                    );

                    return (
                      <Card
                        key={invitation.id}
                        variant="outlined"
                        sx={{
                          bgcolor: "background.level1",
                          borderColor:
                            daysUntilExpiration <= 1
                              ? "warning.300"
                              : "neutral.200",
                        }}
                      >
                        <CardContent>
                          <Stack spacing={2}>
                            {/* Organization and Role Info */}
                            <Box>
                              <Typography
                                level="title-md"
                                sx={{ mb: 0.5, fontWeight: 600 }}
                              >
                                {organizationNames[
                                  invitation.organization_id
                                ] || "Loading..."}
                              </Typography>
                              <Typography level="body-sm" color="neutral">
                                Invited by {invitation.invited_by_email}
                              </Typography>
                              <Typography level="body-sm" color="neutral">
                                Role:{" "}
                                <strong>
                                  {invitation.role === "admin"
                                    ? "Administrator"
                                    : "Member"}
                                </strong>
                              </Typography>
                            </Box>

                            {/* Optional Message */}
                            {invitation.message && (
                              <Alert variant="soft" color="neutral">
                                <Typography level="body-sm">
                                  {invitation.message}
                                </Typography>
                              </Alert>
                            )}

                            {/* Expiration Warning */}
                            <Box>
                              {isExpired ? (
                                <Alert color="danger" variant="soft">
                                  <Typography level="body-sm">
                                    <strong>
                                      This invitation has expired.
                                    </strong>
                                  </Typography>
                                </Alert>
                              ) : daysUntilExpiration <= 1 ? (
                                <Alert color="warning" variant="soft">
                                  <Typography level="body-sm">
                                    <strong>
                                      Expires in {daysUntilExpiration} day
                                      {daysUntilExpiration !== 1 ? "s" : ""}!
                                    </strong>
                                  </Typography>
                                </Alert>
                              ) : (
                                <Typography level="body-sm" color="neutral">
                                  Expires in {daysUntilExpiration} days
                                </Typography>
                              )}
                            </Box>

                            {/* Error Message */}
                            {invitationError[invitation.id] && (
                              <Alert color="danger" variant="soft">
                                <Typography level="body-sm">
                                  {invitationError[invitation.id]}
                                </Typography>
                              </Alert>
                            )}

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={2}>
                              <Button
                                variant="solid"
                                color="primary"
                                onClick={() =>
                                  handleAcceptInvitation(invitation)
                                }
                                disabled={
                                  isExpired ||
                                  processingInvitation === invitation.id ||
                                  invitation.status !== "pending"
                                }
                                loading={processingInvitation === invitation.id}
                                fullWidth
                              >
                                {invitation.status === "accepted"
                                  ? "Accepted"
                                  : "Accept Invitation"}
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {/* Workspaces Section */}
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography level="h3">Workspaces</Typography>
              {isActiveOrganization && (
                <Tooltip
                  title={
                    !canCreate
                      ? `Workspace limit reached (${capacityInfo?.current_count ?? 0}/${capacityInfo?.max_count ?? 0}). Upgrade to create more.`
                      : ""
                  }
                  placement="top"
                >
                  <span ref={addWorkspaceButtonRef}>
                    <Button
                      onClick={handleAddNewClick}
                      startDecorator={<Add />}
                      disabled={creatingWorkspace || checkingCapacity}
                      loading={creatingWorkspace || checkingCapacity}
                      color={!canCreate ? "neutral" : "primary"}
                      size="sm"
                    >
                      Add New
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            {!isActiveOrganization ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography level="body-md" color="neutral">
                  You must be part of an active organization to view workspaces.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Workspaces grouped by module */}
                <Box>
                  {isDevFeaturesEnabled() && (
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="warning"
                        size="sm"
                        startDecorator={<BugReportIcon />}
                        onClick={handleAddFakePausedWorkspaces}
                        title="Dev mode: Add fake paused workspaces for testing"
                      >
                        Add Fake Paused
                      </Button>
                    </Box>
                  )}

                  {loadingWorkspaces ? (
                    <Loading />
                  ) : workspacesFetchError ? (
                    <Sheet
                      variant="soft"
                      color="danger"
                      sx={{
                        borderRadius: "sm",
                        p: { xs: 3, md: 6 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Typography level="title-md" color="danger">
                        Couldn&apos;t load your workspaces
                      </Typography>
                      <Typography
                        level="body-sm"
                        color="neutral"
                        sx={{ maxWidth: "600px" }}
                      >
                        {workspacesFetchError.message ||
                          "An unexpected error occurred."}{" "}
                        Check your connection and try again. If the problem
                        persists, contact support.
                      </Typography>
                      <Button
                        onClick={() => fetchWorkspaces(true)}
                        variant="solid"
                        color="primary"
                        size="md"
                        loading={loadingWorkspaces}
                      >
                        Retry
                      </Button>
                    </Sheet>
                  ) : userWorkspaces.length === 0 ? (
                    <Sheet
                      variant="soft"
                      color="neutral"
                      sx={{
                        borderRadius: "sm",
                        p: { xs: 3, md: 6 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <Typography
                        level="body-md"
                        color="neutral"
                        sx={{
                          maxWidth: "600px",
                          lineHeight: 1.6,
                        }}
                      >
                        Welcome to X-Machine! This application is organised into
                        workspaces that allow you to work on different kinds of
                        activities - limited only by your imagination. Get
                        started by clicking the button to create your first
                        workspace.
                      </Typography>
                      <Tooltip
                        title={
                          !canCreate
                            ? `Workspace limit reached (${capacityInfo?.current_count ?? 0}/${capacityInfo?.max_count ?? 0}). Upgrade to create more.`
                            : ""
                        }
                        placement="top"
                      >
                        <span>
                          <Button
                            onClick={handleAddNewClick}
                            startDecorator={<Add />}
                            disabled={creatingWorkspace || checkingCapacity}
                            loading={creatingWorkspace || checkingCapacity}
                            size="lg"
                            variant="solid"
                            color={!canCreate ? "neutral" : "primary"}
                          >
                            Create Your First Workspace
                          </Button>
                        </span>
                      </Tooltip>
                    </Sheet>
                  ) : (
                    <Stack spacing={2.5}>
                      {workspacesByModule.map((group) => (
                        <Sheet
                          key={group.moduleId}
                          variant="outlined"
                          sx={{
                            borderRadius: "md",
                            overflow: "hidden",
                            borderLeft: "3px solid",
                            borderLeftColor: "primary.400",
                          }}
                        >
                          {/* Module group header */}
                          <Box
                            sx={{
                              px: 2.5,
                              py: 1.5,
                              bgcolor: "background.level1",
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{ color: "primary.500", display: "flex" }}
                              >
                                {getModuleIcon(group.moduleId)}
                              </Box>
                              <Typography
                                level="title-sm"
                                sx={{
                                  fontWeight: 600,
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {group.moduleName}
                              </Typography>
                            </Box>
                            <Typography level="body-xs" color="neutral">
                              {group.workspaces.length} workspace
                              {group.workspaces.length !== 1 ? "s" : ""}
                            </Typography>
                          </Box>

                          {/* Workspace rows */}
                          <Box sx={{ py: 0.5 }}>
                            {group.workspaces.map((workspace, index) => (
                              <WorkspaceRow
                                key={workspace.id}
                                workspace={workspace}
                                even={index % 2 === 0}
                              />
                            ))}
                          </Box>
                        </Sheet>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Paused Workspaces */}
                {(myPausedWorkspaces.length > 0 ||
                  otherPausedWorkspaces.length > 0) && (
                  <Box sx={{ mt: 4 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography level="title-lg">
                        Paused Workspaces
                      </Typography>
                      <Tooltip
                        title="These workspaces were paused because they use modules that were removed from your plan. They are now read-only. Upgrade your plan to re-enable them."
                        variant="soft"
                        placement="top"
                      >
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          sx={{ p: 0.5 }}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {/* Combine paused workspaces (my + other) */}
                    {[...myPausedWorkspaces, ...otherPausedWorkspaces]
                      .length === 0 ? (
                      <Box sx={{ py: 3, textAlign: "center" }}>
                        <Typography level="body-sm" color="neutral">
                          No paused workspaces.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <Box
                          sx={{
                            display: { xs: "none", md: "block" },
                            overflow: "auto",
                          }}
                        >
                          <Table>
                            <thead>
                              <tr>
                                <th>Workspace</th>
                                <th>Status</th>
                                <th style={{ width: "100px" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                ...myPausedWorkspaces,
                                ...otherPausedWorkspaces,
                              ].map((workspace) => {
                                const permissions = getPermissions(workspace);
                                return (
                                  <tr key={workspace.id}>
                                    <td>
                                      <Typography
                                        level="body-md"
                                        sx={{
                                          color: "neutral.400",
                                          textDecoration: "line-through",
                                        }}
                                      >
                                        {workspace.name}
                                      </Typography>
                                    </td>
                                    <td>
                                      <Chip
                                        size="sm"
                                        variant="soft"
                                        color="neutral"
                                      >
                                        Paused
                                      </Chip>
                                    </td>
                                    <td>
                                      {permissions.isOwner && (
                                        <IconButton
                                          onClick={() =>
                                            openDeleteWorkspaceModal(
                                              workspace.id,
                                            )
                                          }
                                          size="sm"
                                          variant="plain"
                                          color="danger"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Box>

                        {/* Mobile Card View */}
                        <Box sx={{ display: { xs: "block", md: "none" } }}>
                          <Stack spacing={2}>
                            {[
                              ...myPausedWorkspaces,
                              ...otherPausedWorkspaces,
                            ].map((workspace) => (
                              <Card
                                key={workspace.id}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  opacity: 0.6,
                                  bgcolor: "background.level1",
                                }}
                              >
                                <Stack spacing={2}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    spacing={2}
                                  >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        level="title-sm"
                                        sx={{
                                          fontWeight: 600,
                                          wordBreak: "break-word",
                                          lineHeight: 1.3,
                                          color: "neutral.400",
                                          textDecoration: "line-through",
                                        }}
                                      >
                                        {workspace.name}
                                      </Typography>
                                    </Box>
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color="neutral"
                                    >
                                      Paused
                                    </Chip>
                                  </Stack>

                                  {getPermissions(workspace).isOwner && (
                                    <Stack
                                      direction="row"
                                      justifyContent="flex-end"
                                    >
                                      <IconButton
                                        onClick={() =>
                                          openDeleteWorkspaceModal(workspace.id)
                                        }
                                        size="sm"
                                        variant="plain"
                                        color="danger"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Stack>
                                  )}
                                </Stack>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {isActiveOrganization && (
          <PublicWorkspacesSection
            workspaces={publicWorkspaces}
            error={publicWorkspaceError}
            joiningWorkspaceId={joiningPublicWorkspace}
            onJoin={handleJoinPublicWorkspace}
          />
        )}

        <RecentlyDeletedWorkspaces
          refreshKey={recoveryRefreshKey}
          onRestored={async () => {
            await fetchWorkspaces(true);
            getWorkspaceCapacity()
              .then(setCapacityInfo)
              .catch(() => {});
          }}
        />

        {/* Access Requests Card - Only visible to admins with pending requests */}
        {isAdmin && accessRequests.length > 0 && (
          <Card>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2 }}>
                Access Requests
              </Typography>
              <AccessRequestsList
                accessRequests={accessRequests}
                loading={loadingAccessRequests}
                approvingRequest={approvingRequest}
                rejectingRequest={rejectingRequest}
                onApprove={handleOpenApproveModal}
                onReject={handleOpenRejectModal}
              />
            </CardContent>
          </Card>
        )}

        {/* Organization and Diagnostics - Side by Side on Wide Screens */}
        <Grid container spacing={0} sx={{ gap: 0 }}>
          {/* Organization Card */}
          <Grid xs={12} lg={6} sx={{ pr: { lg: 1.5 }, mb: { xs: 3, lg: 0 } }}>
            <OrganizationCard
              user={user!}
              hasOrganization={hasOrganization}
              hasPendingAccessRequest={hasPendingAccessRequest}
              isAdmin={isAdmin}
              orgStatus={orgStatus}
              organizationName={
                organizationDetails?.name ||
                user?.organization_name ||
                undefined
              }
              loadingOrganizationDetails={loadingOrganizationDetails}
              pendingInvitations={pendingInvitations}
              loadingInvitations={loadingInvitations}
              organizationNames={organizationNames}
              invitationError={invitationError}
              processingInvitation={processingInvitation}
              availableOrganizations={availableOrganizations}
              onAcceptInvitation={handleAcceptInvitation}
              onOpenRequestAccessModal={handleOpenRequestAccessModal}
              onCreateOrganization={() => setIsOrgModalOpen(true)}
              getDaysUntilExpiration={getDaysUntilExpiration}
              isInvitationExpired={isInvitationExpired}
            />
          </Grid>

          {/* Diagnostics Card - Visible to all users with organization */}
          {hasOrganization && (
            <Grid xs={12} lg={6} sx={{ pl: { lg: 1.5 } }}>
              <DiagnosticsCard
                resourceUsage={resourceUsage}
                userUsage={userUsage}
                loading={loadingResourceUsage}
                loadingUserUsage={loadingUserUsage}
                isAdmin={isAdmin}
              />
            </Grid>
          )}
        </Grid>
      </Stack>

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        open={isOrgModalOpen}
        onClose={handleCloseOrgModal}
        orgName={tempOrgName}
        onOrgNameChange={setTempOrgName}
        onSubmit={handleCreateOrg}
        creating={creatingOrg}
        error={createOrgError}
      />

      {/* Request Access Modal */}
      <RequestAccessModal
        open={requestAccessModalOpen}
        onClose={handleCloseRequestAccessModal}
        organization={orgToRequestAccess}
        message={requestMessage}
        onMessageChange={setRequestMessage}
        onSubmit={handleRequestAccess}
        requesting={requestingAccess}
        error={requestAccessError}
      />

      {/* Approve Access Request Modal */}
      <ApproveAccessRequestModal
        open={approveModalOpen}
        onClose={handleCloseApproveModal}
        request={requestToApprove}
        approveRole={approveRole}
        onApproveRoleChange={setApproveRole}
        onApprove={handleApproveAccessRequest}
        approving={approvingRequest === requestToApprove?.user_id}
      />

      {/* Reject Access Request Modal */}
      <RejectAccessRequestModal
        open={rejectModalOpen}
        onClose={handleCloseRejectModal}
        request={requestToReject}
        rejectReason={rejectReason}
        onRejectReasonChange={setRejectReason}
        onReject={handleRejectAccessRequest}
        rejecting={rejectingRequest === requestToReject?.user_id}
      />

      {/* Add Workspace Modal */}
      <AddWorkspaceModal
        open={workspaceModalOpen}
        handleAddWorkspace={handleAddWorkspace}
        onClose={() => setWorkspaceModalOpen(false)}
        buttonDisabled={creatingWorkspace}
        buttonLabel={
          creatingWorkspace ? <CircularProgress size="sm" /> : "Add new"
        }
      />

      {/* Delete Workspace Confirmation Modal */}
      <ConfirmationModal
        open={workspaceDeleteModalOpen}
        title="Delete Workspace"
        message={DELETE_WORKSPACE_MESSAGE}
        actionLabel="Delete"
        cancelLabel="Cancel"
        onAction={handleDeleteWorkspaceConfirmed}
        onClose={() => setWorkspaceDeleteModalOpen(false)}
        loading={isProcessingWorkspace}
        spinner={<CircularProgress size="sm" />}
      />

      {/* Leave Workspace Confirmation Modal */}
      <ConfirmationModal
        open={workspaceLeaveModalOpen}
        title="Leave Workspace"
        message="Are you sure you want to leave this workspace? This action cannot be undone."
        actionLabel="Leave"
        cancelLabel="Cancel"
        onAction={handleLeaveWorkspaceConfirmed}
        onClose={() => setWorkspaceLeaveModalOpen(false)}
        loading={isProcessingWorkspace}
        spinner={<CircularProgress size="sm" />}
      />

      {/* Debug Panel Modal */}
      <Modal open={isDebugPanelOpen} onClose={() => setIsDebugPanelOpen(false)}>
        <ModalDialog
          size="lg"
          variant="outlined"
          sx={{
            maxWidth: 1000,
            width: "100%",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <DialogTitle>
            <Typography level="h3">Debug Panel - Mock Users</Typography>
          </DialogTitle>

          <Stack spacing={3}>
            <Alert color="primary" variant="soft">
              <Typography level="body-sm">
                Use this panel to view and reset mock users for testing. This
                only works with the Mock API (when backend is unavailable).
              </Typography>
            </Alert>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography level="h4">Mock Users</Typography>
                <Button
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  onClick={fetchDebugUsers}
                  loading={debugLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="solid"
                  color="warning"
                  size="sm"
                  onClick={resetAllUsers}
                >
                  Reset All Users
                </Button>
              </Stack>

              <Sheet
                variant="outlined"
                sx={{
                  height: 600,
                  overflow: "auto",
                  borderRadius: "sm",
                }}
              >
                <Table stickyHeader hoverRow>
                  <thead>
                    <tr>
                      <th
                        style={{
                          minWidth: "250px",
                          width: "35%",
                          backgroundColor:
                            "var(--joy-palette-background-surface)",
                        }}
                      >
                        Email
                      </th>
                      <th
                        style={{
                          backgroundColor:
                            "var(--joy-palette-background-surface)",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          backgroundColor:
                            "var(--joy-palette-background-surface)",
                        }}
                      >
                        Organization
                      </th>
                      <th
                        style={{
                          width: "10%",
                          backgroundColor:
                            "var(--joy-palette-background-surface)",
                        }}
                      >
                        Role
                      </th>
                      <th
                        style={{
                          width: "12%",
                          backgroundColor:
                            "var(--joy-palette-background-surface)",
                        }}
                      >
                        Subscription
                      </th>
                      <th
                        style={{
                          backgroundColor:
                            "var(--joy-palette-background-surface)",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugUsers.map((mockUser: any) => (
                      <tr key={mockUser.id}>
                        <td style={{ minWidth: "250px" }}>{mockUser.email}</td>
                        <td>
                          <Chip size="sm" variant="soft">
                            {mockUser.status}
                          </Chip>
                        </td>
                        <td>{mockUser.organization_name || "None"}</td>
                        <td style={{ width: "10%" }}>
                          {mockUser.role_in_org || "-"}
                        </td>
                        <td style={{ width: "12%" }}>
                          {mockUser.subscription_type || "-"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => resetUser(mockUser.email)}
                          >
                            Reset
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </Box>

            <Alert color="success" variant="soft">
              <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                Current User
              </Typography>
              <Typography level="body-sm">
                Email: {user.email}
                <br />
                Organization: {organizationDetails?.name || "None"}
                <br />
                Role: {user.role_in_org || "Individual"}
                <br />
                Status: {organizationDetails?.status || "N/A"}
              </Typography>
            </Alert>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Upgrade Modal for Workspace Limit */}
      <ConfirmationModal
        open={upgradeModalOpen}
        title="Workspace Limit Reached"
        message={`You have reached your workspace limit (${capacityInfo?.current_count ?? 0}/${capacityInfo?.max_count ?? 0}). Please upgrade your subscription to create more workspaces.`}
        actionLabel="Upgrade"
        cancelLabel="Close"
        onAction={() => {
          setUpgradeModalOpen(false);
          // Navigate to billing/upgrade page
          window.location.href = "/subscription/select-package";
        }}
        onClose={() => setUpgradeModalOpen(false)}
        loading={false}
        spinner={<CircularProgress size="sm" />}
      />
    </Page>
  );
};

export default UserDashboard;
